import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Edit, Trash2 } from "lucide-react";

interface Service {
  id: string;
  slug: string;
  name: string;
  description: string;
  hero_title_template: string;
  hero_subtitle_template: string;
  service_list: string[];
  is_emergency: boolean;
  meta_title_template: string;
  meta_description_template: string;
}

const AdminServices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({
    slug: "",
    name: "",
    description: "",
    hero_title_template: "",
    hero_subtitle_template: "",
    service_list: [],
    is_emergency: false,
    meta_title_template: "",
    meta_description_template: "",
  });

  useEffect(() => {
    if (!user) {
      navigate("/admin/login");
      return;
    }
    fetchServices();
  }, [user, navigate]);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('name');

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast({
        title: "Error",
        description: "Failed to load services",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.slug || !formData.name || !formData.hero_title_template) {
        toast({
          title: "Validation Error",
          description: "Please fill in all required fields (slug, name, hero title)",
          variant: "destructive",
        });
        return;
      }

      const dataToSave = {
        slug: formData.slug,
        name: formData.name,
        description: formData.description,
        hero_title_template: formData.hero_title_template,
        hero_subtitle_template: formData.hero_subtitle_template,
        service_list: formData.service_list || [],
        is_emergency: formData.is_emergency || false,
        meta_title_template: formData.meta_title_template,
        meta_description_template: formData.meta_description_template,
      };

      if (editing) {
        const { error } = await supabase
          .from('services')
          .update(dataToSave)
          .eq('id', editing);

        if (error) throw error;
        toast({ title: "Service updated successfully" });
      } else {
        const { error } = await supabase
          .from('services')
          .insert([dataToSave]);

        if (error) throw error;
        toast({ title: "Service created successfully" });
      }

      setEditing(null);
      setFormData({
        slug: "",
        name: "",
        description: "",
        hero_title_template: "",
        hero_subtitle_template: "",
        service_list: [],
        is_emergency: false,
        meta_title_template: "",
        meta_description_template: "",
      });
      fetchServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: "Error",
        description: "Failed to save service",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this service?")) return;

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Service deleted successfully" });
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: "Error",
        description: "Failed to delete service",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (service: Service) => {
    setEditing(service.id);
    setFormData(service);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Manage Services</h1>
          <p className="text-muted-foreground">Create and manage service landing pages</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-6">
              {editing ? "Edit Service" : "Add New Service"}
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="slug">URL Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="roof-repair"
                />
              </div>

              <div>
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Roof Repair"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the service"
                />
              </div>

              <div>
                <Label htmlFor="hero_title">Hero Title Template</Label>
                <Input
                  id="hero_title"
                  value={formData.hero_title_template}
                  onChange={(e) => setFormData({ ...formData, hero_title_template: e.target.value })}
                  placeholder="Expert {service} in {city}"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use {"{city}"} and {"{county}"} as placeholders
                </p>
              </div>

              <div>
                <Label htmlFor="hero_subtitle">Hero Subtitle Template</Label>
                <Input
                  id="hero_subtitle"
                  value={formData.hero_subtitle_template}
                  onChange={(e) => setFormData({ ...formData, hero_subtitle_template: e.target.value })}
                  placeholder="Serving {county} County"
                />
              </div>

              <div>
                <Label htmlFor="services">Service List (comma-separated)</Label>
                <Textarea
                  id="services"
                  value={formData.service_list?.join(', ')}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    service_list: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="Service 1, Service 2, Service 3"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="emergency"
                  checked={formData.is_emergency}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_emergency: checked })}
                />
                <Label htmlFor="emergency">24/7 Emergency Service</Label>
              </div>

              <div>
                <Label htmlFor="meta_title">Meta Title Template</Label>
                <Input
                  id="meta_title"
                  value={formData.meta_title_template}
                  onChange={(e) => setFormData({ ...formData, meta_title_template: e.target.value })}
                  placeholder="{service} in {city} | Company Name"
                />
              </div>

              <div>
                <Label htmlFor="meta_description">Meta Description Template</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description_template}
                  onChange={(e) => setFormData({ ...formData, meta_description_template: e.target.value })}
                  placeholder="Professional {service} in {city}..."
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} className="flex-1">
                  {editing ? "Update Service" : "Add Service"}
                </Button>
                {editing && (
                  <Button variant="outline" onClick={() => {
                    setEditing(null);
                    setFormData({
                      slug: "",
                      name: "",
                      description: "",
                      hero_title_template: "",
                      hero_subtitle_template: "",
                      service_list: [],
                      is_emergency: false,
                      meta_title_template: "",
                      meta_description_template: "",
                    });
                  }}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Services List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold">Existing Services</h2>
            {services.map((service) => (
              <Card key={service.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold">{service.name}</h3>
                    <p className="text-sm text-muted-foreground">/{service.slug}/[city]</p>
                    {service.is_emergency && (
                      <span className="inline-block mt-2 text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                        24/7 Emergency
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(service)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(service.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{service.description}</p>
                <p className="text-xs text-muted-foreground">
                  {service.service_list.length} services listed
                </p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminServices;
